import { Box, Button, Menu, MenuItem, Tooltip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { lightPrimary } from "src/colors";
import { useSiteAdminsProfile } from "src/features/site-management/hooks/api/admins/use-site-admins-profile";
import { useDialog } from "src/hooks/use-dialog";
import { usePopover } from "src/hooks/use-popover";
import useStore from "src/hooks/use-store";
import { ChevronDown as ChevronDownIcon } from "src/icons/chevron-down";
import { Plus } from "src/icons/plus";
import { Can } from "src/permissions/can";
import { OrganizationRegistrationType } from "src/types/enum/organization-registration-type";
import { OnboardingDialog } from "./onboarding/onboarding-dialog";
import { useEffect, useMemo, useRef, useState } from "react";
import { Query } from "../atoms/query";
import { Scrollbar } from "../atoms/scrollbar";
import { globalStatisticsKeys } from "src/features/statistics/shared/api/query-keys/global-statistics-keys";

const SEARCH_INPUT_THRESHOLD = 5;

const SiteSelectorComponent = () => {
  const { user } = useStore();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [anchorRef, open, handleOpen, handleClose] = usePopover();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const firstMenuItemRef = useRef<HTMLLIElement | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const siteProfileQuery = useSiteAdminsProfile();

  const selectedSite = siteProfileQuery.data?.sites.find(site => site.token === user.siteToken);

  const [isOnboardingDialogOpen, handleOpenOnboardingDialog, handleCloseOnboardingDialog] =
    useDialog();

  const isSearchInputThresholdCrossed = useMemo(
    () => siteProfileQuery?.data?.sites?.length > SEARCH_INPUT_THRESHOLD,
    [siteProfileQuery],
  );

  const filteredSites = useMemo(
    () =>
      siteProfileQuery.data?.sites?.filter(item =>
        item.name.toLowerCase().includes(searchQuery.trim().toLowerCase()),
      ),
    [siteProfileQuery, searchQuery],
  );

  useEffect(() => {
    if (!open) return;

    setTimeout(() => {
      isSearchInputThresholdCrossed ? inputRef.current?.focus() : firstMenuItemRef.current?.focus();
    });
  }, [open, isSearchInputThresholdCrossed]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: {
            sm: "center",
            md: "flex-start",
          },
          flexDirection: {
            sm: "row",
            md: "column",
          },
          justifyContent: {
            sm: "flex-start",
            md: "space-between",
          },
          gap: {
            sm: "10px",
            md: "0",
          },
        }}>
        <>
          <Button
            color="primary"
            onClick={handleOpen}
            ref={anchorRef}
            size="large"
            variant="text"
            endIcon={
              <ChevronDownIcon
                fontSize="small"
                sx={{
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease-in",
                }}
              />
            }>
            {selectedSite?.name ?? "---"}
          </Button>
          <Menu
            anchorEl={anchorRef.current}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            open={open}
            onClose={() => {
              handleClose();
              setSearchQuery("");
            }}
            slotProps={{
              list: {
                sx: {
                  ...(isSearchInputThresholdCrossed ? { pt: 0 } : {}),
                },
              },
              paper: {
                sx: {
                  border: "1px solid",
                  borderColor: "divider",
                  mt: 0,
                },
              },
            }}>
            {isSearchInputThresholdCrossed && (
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "background.paper",
                  zIndex: 1,
                  outline: "none",
                  borderBottom: `1px solid`,
                  borderColor: "divider",
                }}
                onFocus={() => {
                  inputRef.current?.focus();
                }}>
                <Query
                  textInputRef={inputRef}
                  disabled={false}
                  searchAsType
                  onChange={newQuery => setSearchQuery(newQuery)}
                  value={searchQuery}
                  sx={{
                    border: "none",
                    borderRadius: 0,
                    py: 2.5,
                    minWidth: "100%",
                    width: "min-content",
                  }}
                  onKeyDown={e => {
                    if (e.key !== "ArrowDown") {
                      e.stopPropagation();
                    } else {
                      setTimeout(() => {
                        firstMenuItemRef.current?.focus();
                      }, 0);
                    }
                  }}
                />
              </Box>
            )}

            <Scrollbar
              sx={{
                maxHeight: "60vh",
                width: "max-content",
                pt: isSearchInputThresholdCrossed ? 1 : 0,
              }}>
              {filteredSites?.length > 0 ? (
                filteredSites?.map((site, idx) => {
                  return (
                    <MenuItem
                      ref={idx === 0 ? firstMenuItemRef : null}
                      key={site.token}
                      onClick={() => {
                        user.setSiteToken(site.token);
                        user.setSiteTimeZone(site?.timezone || "Europe/Warsaw");

                        if (location.pathname === "/statistics") {
                          //for the statistics page only, delay invalidating stats keys in order to prevent fetch error with invalid filters - filters are being reset in the statistics page, then stats query keys are invalidated. to be reviewed upon context query keys fixing
                          queryClient.invalidateQueries({
                            predicate: query => {
                              return query.queryKey[0] !== globalStatisticsKeys.all[0];
                            },
                          });
                        } else {
                          setTimeout(() => {
                            queryClient.invalidateQueries();
                          }, 200);
                        }

                        navigate(`/statistics`);
                        handleClose();
                        setSearchQuery("");
                      }}
                      sx={{ minWidth: "fit-content" }}>
                      {site.name}
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem disabled>{t("common:noResults")}</MenuItem>
              )}
            </Scrollbar>
          </Menu>
        </>
        <Can I="create" a="Sites" passThrough>
          {allowed => (
            <Tooltip title={t("common:notSufficientPermissions")} disableHoverListener={allowed}>
              <span>
                <Button
                  onClick={handleOpenOnboardingDialog}
                  size="small"
                  variant="text"
                  disabled={!allowed}
                  startIcon={<Plus />}
                  sx={{
                    color: lightPrimary.hoverOutlined,
                    pb: 0,
                    px: 0,
                    borderRadius: "unset",
                    borderBottom: `1px solid ${lightPrimary.hoverOutlined}`,
                    ":hover": { color: "#65A5BA", borderColor: `#65A5BA` },
                    ":disabled": { color: " #8E8E8E", borderColor: `#8E8E8E` },
                  }}>
                  {t("sites:addNewSiteButton")}
                </Button>
              </span>
            </Tooltip>
          )}
        </Can>
      </Box>
      <OnboardingDialog
        isOpen={isOnboardingDialogOpen}
        onClose={handleCloseOnboardingDialog}
        canCancelOnboardingProcess
        role={OrganizationRegistrationType.SITE_OWNER}
      />
    </>
  );
};

export const SiteSelector = observer(SiteSelectorComponent);
